import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { DurationFormatter } from '@blade/ui-kit'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'

import { TrackingWalletsTableTagsFilter } from './filters/tracking-wallets-table-tags-filter'
import { TrackingWalletCell } from './tracking-wallet-cell'
import { TrackingWalletTagsCell } from './tracking-wallet-tags-cell'
import { TrackingWalletsActions } from './tracking-wallets-actions'

import type { TrackingWalletsTableColumnId } from '../../atoms/reatom-tracking-wallets-table'
import type { TrackingWallet } from '@root/entities/wallet-tracking'
import type { Column } from '@root/shared/ui-kit'

const CreatedAtCell: React.FC<{ value: Date }> = ({ value }) => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()
  const createdAt = useMemo(
    () =>
      DurationFormatter.formatDurationSingleUnit(
        DateF.intervalToDuration({ start: value, end: new Date() }),
        { locale },
      ),
    [value, locale],
  )
  const createdAtFormatted = createdAt
    ? t('common.ago', { time: createdAt })
    : t('common.now')
  return <CreatedAtRoot>{createdAtFormatted}</CreatedAtRoot>
}

const CreatedAtRoot = styled.div`
  color: var(--text-9);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

export const trackingWalletsTableColumns: Array<
  Column<TrackingWallet.TrackingWallet, TrackingWalletsTableColumnId>
> = [
  {
    id: 'created-at',
    header: ({ t }) => t('common.created'),
    render: ({ value }) => <CreatedAtCell value={value.createdAt} />,
    minWidth: 120,
    maxWidth: 220,
  },
  {
    id: 'name',
    header: ({ t }) => t('wallet-tracker.table.label'),
    render: ({ value }) => <TrackingWalletCell wallet={value} />,
    minWidth: 240,
  },
  {
    id: 'tags',
    header: (args) => <TrackingWalletsTableTagsFilter {...args} />,
    render: ({ value }) => <TrackingWalletTagsCell wallet={value} />,
    width: 240,
    justify: 'stretch',
    filterable: true,
  },
  {
    id: 'actions',
    justify: 'end',
    header: ({ t }) => t('common.action'),
    render: ({ value }) => <TrackingWalletsActions wallet={value} />,
    minWidth: 120,
    maxWidth: 220,
  },
]
